import React from "react";
import { CommentNode } from "./types";
import {
  formatCommentTime,
  getCommentDisplayExcerpt,
  getCommentDisplayText,
  getCommentScopeClass,
  getCommentScopeLabel,
} from "./commentText";

interface CommentThreadsViewProps {
  roots: CommentNode[];
  selectedCommentId: number | null;
  currentEditId?: number | null;
  onSelect: (commentId: number) => void;
  onOpenReader: (commentId: number) => void;
  onOpenMap: (commentId: number) => void;
}

interface ThreadNodeCardProps {
  comment: CommentNode;
  level: number;
  selectedCommentId: number | null;
  currentEditId?: number | null;
  onSelect: (commentId: number) => void;
  onOpenReader: (commentId: number) => void;
  onOpenMap: (commentId: number) => void;
}

const MAX_VISIBLE_LEVEL = 5;

const ThreadNodeCard: React.FC<ThreadNodeCardProps> = ({
  comment,
  level,
  selectedCommentId,
  currentEditId,
  onSelect,
  onOpenReader,
  onOpenMap,
}) => {
  const scopeClass = getCommentScopeClass(comment, currentEditId);
  const selected = comment.id === selectedCommentId;
  const visualLevel = Math.min(level, MAX_VISIBLE_LEVEL);
  const hasReplies = comment.replies.length > 0;
  const levelStyle = { "--comment-level": visualLevel } as React.CSSProperties & Record<string, number>;

  return (
    <div className="comment-explorer-thread-flow-node" style={levelStyle}>
      <article
        className={`comment-explorer-thread-card comment-explorer-flow-card ${scopeClass} ${selected ? "is-selected is-expanded" : ""} ${comment.isRemoved ? "is-removed" : ""}`}
      >
        <button type="button" className="comment-explorer-card-main" onClick={() => onSelect(comment.id)}>
          <div className="comment-explorer-card-kicker">
            <span className={`comment-explorer-scope-dot ${scopeClass}`} />
            <span>{getCommentScopeLabel(comment, currentEditId)}</span>
            <span>·</span>
            <span>{formatCommentTime(comment.createdAt)}</span>
            <span>·</span>
            <span>{comment.user?.name || "Unknown user"}</span>
          </div>

          <h3>{comment.isRemoved ? "Removed comment" : getCommentDisplayExcerpt(comment, selected ? 130 : 74)}</h3>
          <p>{selected ? getCommentDisplayText(comment) : getCommentDisplayExcerpt(comment, 180)}</p>
        </button>

        <div className="comment-explorer-thread-meta">
          <span>{comment.directReplyCount} direct</span>
          <span>{comment.totalReplyCount} total</span>
          <span>{hasReplies ? `${comment.replies.length} branches` : "single branch"}</span>
        </div>

        {selected && (
          <div className="comment-explorer-thread-actions">
            <button type="button" onClick={() => onOpenReader(comment.id)}>
              Open line in Reader
            </button>
            <button type="button" onClick={() => onOpenMap(comment.id)}>
              Open in Map
            </button>
          </div>
        )}
      </article>

      {hasReplies && (
        <div className="comment-explorer-thread-children">
          {comment.replies.map((reply) => (
            <ThreadNodeCard
              key={reply.id}
              comment={reply}
              level={level + 1}
              selectedCommentId={selectedCommentId}
              currentEditId={currentEditId}
              onSelect={onSelect}
              onOpenReader={onOpenReader}
              onOpenMap={onOpenMap}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CommentThreadsView: React.FC<CommentThreadsViewProps> = ({
  roots,
  selectedCommentId,
  currentEditId,
  onSelect,
  onOpenReader,
  onOpenMap,
}) => {
  return (
    <div className="comment-explorer-threads-view comment-explorer-overview-view" aria-label="Reddit-style threaded comment overview">
      <div className="comment-explorer-view-note">
        Overview shows the full Reddit-style comment flow. Single-click any comment to expand it in place.
      </div>

      {roots.map((root) => (
        <ThreadNodeCard
          key={root.id}
          comment={root}
          level={0}
          selectedCommentId={selectedCommentId}
          currentEditId={currentEditId}
          onSelect={onSelect}
          onOpenReader={onOpenReader}
          onOpenMap={onOpenMap}
        />
      ))}
    </div>
  );
};

export default CommentThreadsView;
